import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { CASE_STUDIES, CONTACT } from "@/data/site";

export default function CaseStudyDetail() {
  const { id } = useParams();
  const index = CASE_STUDIES.findIndex((s) => s.id === id);
  if (index === -1) return <Navigate to="/case-studies" replace />;

  const study = CASE_STUDIES[index];
  const prev = CASE_STUDIES[(index - 1 + CASE_STUDIES.length) % CASE_STUDIES.length];
  const next = CASE_STUDIES[(index + 1) % CASE_STUDIES.length];

  return (
    <>
      <Seo
        title={`${study.title} — ${study.category} Case Study — codeRealm`}
        siteName="codeRealm"
        description={study.summary}
        image={study.image}
      />
      <article data-testid="case-study-detail" className="mx-auto max-w-7xl px-5 pb-24 pt-16 sm:px-8 sm:pt-24">
        <Reveal y={12}>
          <Link
            to="/case-studies"
            data-testid="back-to-case-studies-link"
            className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground transition-colors duration-300 hover:text-coral"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-0.5" />
            All case studies
          </Link>
        </Reveal>

        <div className="mt-10">
          <MaskedLine>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-coral">
              {study.industry}
            </span>
          </MaskedLine>
          <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl">
            <MaskedLine delay={0.12}>
              {study.title}
              <span className="text-coral">.</span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.25} className="mt-4">
            <p className="font-display text-xl text-muted-foreground sm:text-2xl">{study.subtitle}</p>
          </Reveal>
        </div>

        <Reveal delay={0.35} className="mt-12 overflow-hidden rounded-2xl border border-border">
          <div className="aspect-[21/9] w-full overflow-hidden">
            <img
              src={study.image}
              alt={`${study.title} — ${study.subtitle}`}
              className="h-full w-full object-cover"
              data-testid="case-study-hero-image"
            />
          </div>
        </Reveal>

        {/* Hide metrics until real numbers land */}
        {study.metrics.length > 0 && (
          <Reveal className="mt-10 grid gap-6 rounded-2xl border border-border bg-card p-8 sm:grid-cols-3 sm:p-10">
            {study.metrics.map((m) => (
              <div key={m.label} data-testid={`metric-${m.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="font-display text-3xl font-extrabold text-coral sm:text-4xl">{m.value}</div>
                <div className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                  {m.label}
                </div>
              </div>
            ))}
          </Reveal>
        )}

        <div className="mt-16 grid gap-12 lg:grid-cols-[200px_1fr]">
          <Reveal>
            <span className="font-display text-5xl font-extrabold text-outline">01</span>
            <h2 className="mt-2 font-mono text-xs uppercase tracking-[0.25em] text-coral">The challenge</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">{study.challenge}</p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-12 lg:grid-cols-[200px_1fr]">
          <Reveal>
            <span className="font-display text-5xl font-extrabold text-outline">02</span>
            <h2 className="mt-2 font-mono text-xs uppercase tracking-[0.25em] text-coral">The solution</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">{study.solution}</p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {study.architecture.map((item, i) => (
                <div
                  key={item}
                  data-testid={`architecture-item-${i}`}
                  className="flex items-center gap-3 rounded-xl border border-border bg-card px-5 py-4 transition-colors duration-300 hover:border-coral/50"
                >
                  <span className="font-mono text-[10px] text-coral">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-16 flex flex-wrap gap-2">
          {study.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full border border-border px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground"
            >
              {tag}
            </span>
          ))}
          <span className="ml-auto font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground/70">
            {study.industry}{study.timeline ? ` · ${study.timeline}` : ""} · {study.year}
          </span>
        </Reveal>

        <Reveal className="mt-20 grid gap-4 border-t border-border pt-10 sm:grid-cols-2">
          <Link
            to={`/case-study/${prev.id}`}
            data-testid="prev-case-study-link"
            className="group rounded-2xl border border-border p-6 transition-colors duration-300 hover:border-coral/60"
          >
            <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-1" /> Previous
            </span>
            <span className="mt-3 block font-display text-xl font-bold tracking-tight">{prev.title}</span>
          </Link>
          <Link
            to={`/case-study/${next.id}`}
            data-testid="next-case-study-link"
            className="group rounded-2xl border border-border p-6 text-right transition-colors duration-300 hover:border-coral/60"
          >
            <span className="flex items-center justify-end gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              Next <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
            <span className="mt-3 block font-display text-xl font-bold tracking-tight">{next.title}</span>
          </Link>
        </Reveal>

        <Reveal className="mt-16 flex flex-col items-start gap-6 rounded-3xl bg-navy p-10 text-white dark:bg-card sm:p-14">
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
            Want results like these?
          </h2>
          <a
            href={`mailto:${CONTACT.email}?subject=${encodeURIComponent(`Project like ${study.title} — inquiry`)}`}
            data-testid="case-study-contact-button"
            className="group flex items-center gap-2 rounded-full bg-coral px-7 py-3.5 font-mono text-xs uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-coral-bright"
          >
            Start the conversation
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </Reveal>
      </article>
    </>
  );
}
